import { Info } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import type { QuickAddOfferPickerProps } from './quick-add-offer-picker';
import type { QuickAddOfferPickerPanelProps } from './quick-add-offer-picker-panel';
import { CreateNewSection } from './quick-add-create-new-section';
import { CreateNewLink, OfferList, SectionLabel, SelectedOfferCard } from './quick-add-offer-list';

export { SelectedOfferCard } from './quick-add-offer-list';

export function QuickAddOfferPickerHeader({
  title,
  subtitle,
}: Pick<QuickAddOfferPickerProps, 'title' | 'subtitle'>) {
  return (
    <div className="space-y-1">
      <h2 className="text-base font-semibold leading-tight text-foreground">{title}</h2>
      <p className="text-sm leading-tight text-muted-foreground">{subtitle}</p>
    </div>
  );
}

export function QuickAddOfferPickerBody({
  drafts,
  selectedDraftId,
  clientOptions,
  draftUnitLabel,
  draftSummarySeparator,
  pendingUnitsLabel,
  targetOfferLabel,
  searchPlaceholder,
  createNewLinkLabel,
  changeSelectionLabel,
  cancelChangeLabel,
  backToExistingLabel,
  lastUsedHintLabel,
  noPreselectionLabel,
  noDraftsLabel,
  noPermissionLabel,
  noResultsLabel,
  createNewLabel,
  clientFieldLabel,
  clientFieldPlaceholder,
  salespersonFieldLabel,
  salespersonFieldPlaceholder,
  salespersonFieldLockedHint,
  salespersonFieldDisabled,
  salesUserOptions,
  selectedClientBrands,
  canCreateClient,
  onClientCreated,
  addCoCreators,
  onAddCoCreatorsChange,
  campaignDurationLabel,
  campaignDurationPlaceholder,
  campaignDurationInvalidRangeLabel,
  openFullFormLabel,
  openFullFormHint,
  validationClientRequiredLabel,
  mode,
  createDraftForm,
  onCreateDraftSubmit,
  onSelectDraft,
  onModeChange,
  onOpenFullForm,
  lockedTarget,
  campaignNumberPreview,
}: QuickAddOfferPickerPanelProps) {
  const locked = lockedTarget != null;
  const selectedDraft = drafts.find((draft) => draft.id === selectedDraftId) ?? null;

  if (mode === 'creating') {
    return (
      <CreateNewSection
        createNewLabel={createNewLabel}
        clientFieldLabel={clientFieldLabel}
        clientFieldPlaceholder={clientFieldPlaceholder}
        salespersonFieldLabel={salespersonFieldLabel}
        salespersonFieldPlaceholder={salespersonFieldPlaceholder}
        salespersonFieldLockedHint={salespersonFieldLockedHint}
        salespersonFieldDisabled={salespersonFieldDisabled}
        salesUserOptions={salesUserOptions}
        selectedClientBrands={selectedClientBrands}
        canCreateClient={canCreateClient}
        onClientCreated={onClientCreated}
        addCoCreators={addCoCreators}
        onAddCoCreatorsChange={onAddCoCreatorsChange}
        campaignDurationLabel={campaignDurationLabel}
        campaignDurationPlaceholder={campaignDurationPlaceholder}
        campaignDurationInvalidRangeLabel={campaignDurationInvalidRangeLabel}
        clientOptions={clientOptions}
        openFullFormLabel={openFullFormLabel}
        openFullFormHint={openFullFormHint}
        validationClientRequiredLabel={validationClientRequiredLabel}
        backToExistingLabel={backToExistingLabel}
        createDraftForm={createDraftForm}
        onBack={() => onModeChange(selectedDraft != null ? 'summary' : 'changing')}
        onSubmit={onCreateDraftSubmit}
        onOpenFullForm={onOpenFullForm}
        campaignNumberPreview={campaignNumberPreview}
      />
    );
  }

  if (mode === 'changing') {
    return (
      <OfferList
        drafts={drafts}
        selectedDraftId={selectedDraftId}
        draftUnitLabel={draftUnitLabel}
        draftSummarySeparator={draftSummarySeparator}
        targetOfferLabel={targetOfferLabel}
        searchPlaceholder={searchPlaceholder}
        createNewLinkLabel={createNewLinkLabel}
        cancelChangeLabel={cancelChangeLabel}
        noDraftsLabel={noDraftsLabel}
        noPermissionLabel={noPermissionLabel}
        noResultsLabel={noResultsLabel}
        onSelectDraft={(draftId) => onSelectDraft?.(draftId)}
        onCreateNew={() => onModeChange('creating')}
        onCancelChange={() => onModeChange(selectedDraft != null ? 'summary' : 'changing')}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SectionLabel>{targetOfferLabel}</SectionLabel>
        {locked ? null : (
          <CreateNewLink label={createNewLinkLabel} onClick={() => onModeChange('creating')} />
        )}
      </div>

      {selectedDraft ? (
        <SelectedOfferCard
          draft={selectedDraft}
          draftUnitLabel={draftUnitLabel}
          draftSummarySeparator={draftSummarySeparator}
          pendingUnitsLabel={pendingUnitsLabel}
          changeSelectionLabel={changeSelectionLabel}
          locked={locked}
          onChange={() => onModeChange('changing')}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
          {noPreselectionLabel}
        </div>
      )}

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        {lastUsedHintLabel}
      </div>
    </div>
  );
}

export function QuickAddOfferPickerFooter({
  mode,
  selectedDraftId,
  drafts,
  confirmExistingLabel,
  confirmNewLabel,
  cancelLabel,
  destinationLabel,
  creationStatusHintLabel,
  formId,
  createDraftConfirmDisabled,
  onExistingConfirm,
  onCancel,
}: Pick<
  QuickAddOfferPickerPanelProps,
  | 'mode'
  | 'selectedDraftId'
  | 'drafts'
  | 'confirmExistingLabel'
  | 'confirmNewLabel'
  | 'cancelLabel'
  | 'destinationLabel'
  | 'creationStatusHintLabel'
  | 'formId'
  | 'createDraftConfirmDisabled'
  | 'onCreateDraftSubmit'
  | 'onExistingConfirm'
  | 'onCancel'
>) {
  const selectedDraft = drafts.find((draft) => draft.id === selectedDraftId) ?? null;
  const confirmLabel = mode === 'creating' ? confirmNewLabel : confirmExistingLabel;
  const confirmDisabled = getQuickAddFooterConfirmDisabled(
    mode,
    createDraftConfirmDisabled,
    selectedDraftId,
  );
  const destinationText = getQuickAddFooterDestinationText(
    mode,
    creationStatusHintLabel,
    destinationLabel,
    selectedDraft?.title,
  );

  return (
    <div className="flex w-full items-center justify-between gap-3">
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        {destinationText}
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel ?? (() => {})}>
          {cancelLabel}
        </Button>
        <Button
          type={mode === 'creating' ? 'submit' : 'button'}
          form={mode === 'creating' ? formId : undefined}
          size="sm"
          variant="destructive"
          className="font-semibold"
          disabled={confirmDisabled}
          onClick={mode === 'creating' ? undefined : (onExistingConfirm ?? (() => {}))}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function getQuickAddFooterConfirmDisabled(
  mode: QuickAddOfferPickerPanelProps['mode'],
  createDraftConfirmDisabled: boolean | undefined,
  selectedDraftId: string | null,
) {
  if (mode === 'creating') {
    return createDraftConfirmDisabled;
  }

  return selectedDraftId == null;
}

function getQuickAddFooterDestinationText(
  mode: QuickAddOfferPickerPanelProps['mode'],
  creationStatusHintLabel: string,
  destinationLabel: string,
  selectedDraftTitle?: string,
) {
  if (mode === 'creating') {
    return creationStatusHintLabel;
  }

  if (selectedDraftTitle) {
    return `${destinationLabel}: ${selectedDraftTitle}`;
  }

  return destinationLabel;
}
